import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { PageTitle } from '../components/PageTitle';
import { Building2, Plus, Trash2, X, Camera, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputCls = "w-full px-4 py-3 rounded-xl text-sm text-white placeholder:text-[#4a4a50] outline-none transition-colors focus:border-[#c9a84c]/60 border border-white/10 bg-white/5";

export const Organizations = () => {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRef = useRef(null);
  const [pendingPictureOrgId, setPendingPictureOrgId] = useState(null);

  useEffect(() => {
    if (['profesional', 'premium'].includes(user?.subscription_plan)) {
      loadOrganizations();
      const saved = localStorage.getItem('selectedOrganization');
      if (saved) setSelectedOrg(JSON.parse(saved));
    }
  }, [user]);

  const loadOrganizations = async () => {
    try {
      const res = await axios.get(`${API}/crm/organizations`);
      setOrganizations(res.data);
    } catch (error) {
      console.error('Error loading organizations:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/crm/organizations`, formData);
      toast.success('Organización creada');
      setOrganizations([res.data, ...organizations]);
      setFormData({ name: '', description: '' });
      setShowForm(false);
    } catch (error) {
      toast.error('Error al crear organización');
    }
  };

  const handleDelete = async (orgId) => {
    if (!window.confirm('¿Eliminar esta organización? Se perderán todos sus datos asociados.')) return;
    try {
      await axios.delete(`${API}/crm/organizations/${orgId}`);
      toast.success('Organización eliminada');
      setOrganizations(organizations.filter(o => o.organization_id !== orgId));
      if (selectedOrg?.organization_id === orgId) {
        setSelectedOrg(null);
        localStorage.removeItem('selectedOrganization');
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleSelectOrg = (org) => {
    setSelectedOrg(org);
    localStorage.setItem('selectedOrganization', JSON.stringify(org));
    toast.success(`"${org.name}" seleccionada`);
  };

  const handlePictureClick = (e, orgId) => {
    e.stopPropagation();
    setPendingPictureOrgId(orgId);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !pendingPictureOrgId) return;
    e.target.value = '';

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) { toast.error('Solo JPG, PNG, WEBP o GIF'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Máximo 5 MB'); return; }

    setUploadingId(pendingPictureOrgId);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/organizations/${pendingPictureOrgId}/picture`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const pictureUrl = res.data.picture;
      setOrganizations(prev => prev.map(o =>
        o.organization_id === pendingPictureOrgId ? { ...o, picture: pictureUrl } : o
      ));
      if (selectedOrg?.organization_id === pendingPictureOrgId) {
        const updated = { ...selectedOrg, picture: pictureUrl };
        setSelectedOrg(updated);
        localStorage.setItem('selectedOrganization', JSON.stringify(updated));
      }
      toast.success('Foto actualizada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir la foto');
    } finally {
      setUploadingId(null);
      setPendingPictureOrgId(null);
    }
  };

  if (!['profesional', 'premium'].includes(user?.subscription_plan)) {
    return (
      <div className="min-h-screen p-12" style={{ background: '#050508' }}>
        <div className="max-w-4xl mx-auto text-center pt-24">
          <Building2 size={64} className="mx-auto mb-6 text-[#c9a84c]" />
          <h1 className="text-4xl font-bold mb-4 text-white">Organizaciones</h1>
          <p className="text-[#7a7870] text-lg">Esta función es exclusiva del plan Profesional</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6" style={{ background: '#050508' }}>
      <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />

      <div className="max-w-6xl mx-auto pt-16">
        <PageTitle className="pt-24">Organizaciones</PageTitle>
        <p className="text-[#7a7870] mb-8">Gestiona múltiples empresas y filtra tus datos por organización</p>

        {/* Org activa */}
        {selectedOrg && (
          <div className="rounded-2xl p-5 mb-8 flex items-center justify-between"
            style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)' }}>
            <div className="flex items-center gap-4">
              {selectedOrg.picture ? (
                <img src={selectedOrg.picture} alt={selectedOrg.name} className="w-12 h-12 rounded-xl object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(201,168,76,0.15)' }}>
                  <Building2 size={22} className="text-[#c9a84c]" />
                </div>
              )}
              <div>
                <p className="text-[#c9a84c] text-xs font-semibold uppercase tracking-wider mb-0.5">Organización activa</p>
                <h3 className="text-white font-bold text-lg">{selectedOrg.name}</h3>
                {selectedOrg.description && <p className="text-[#7a7870] text-sm">{selectedOrg.description}</p>}
              </div>
            </div>
            <button onClick={() => { setSelectedOrg(null); localStorage.removeItem('selectedOrganization'); }}
              className="text-[#7a7870] hover:text-white transition-colors p-2">
              <X size={20} />
            </button>
          </div>
        )}

        {/* Nueva org */}
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 font-bold text-black px-5 py-3 rounded-xl mb-8 transition-all hover:-translate-y-0.5"
          style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}>
          <Plus size={18} />
          Nueva Organización
        </button>

        {showForm && (
          <div className="rounded-2xl p-7 mb-8" style={{ background: '#0d0d14', border: '1px solid rgba(255,255,255,0.07)' }}>
            <h2 className="text-xl font-bold text-white mb-6">Crear Organización</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-[#7a7870] text-xs font-semibold uppercase tracking-wider mb-2">Nombre *</label>
                <input type="text" required value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={inputCls} placeholder="Ej: Tecnocasa, Mi Inmobiliaria..." />
              </div>
              <div>
                <label className="block text-[#7a7870] text-xs font-semibold uppercase tracking-wider mb-2">Descripción</label>
                <textarea value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={inputCls} rows={3} placeholder="Breve descripción..." />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit"
                  className="flex-1 py-3 rounded-xl font-bold text-black transition-all hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}>
                  Crear
                </button>
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 py-3 rounded-xl font-bold text-white transition-all"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Grid orgs */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {organizations.length === 0 ? (
            <div className="col-span-full rounded-2xl p-16 text-center"
              style={{ background: '#0d0d14', border: '1px solid rgba(255,255,255,0.06)' }}>
              <Building2 size={56} className="mx-auto text-[#3a3a40] mb-4" />
              <p className="text-[#7a7870]">No tienes organizaciones creadas</p>
            </div>
          ) : organizations.map(org => {
            const isActive = selectedOrg?.organization_id === org.organization_id;
            const isUploading = uploadingId === org.organization_id;
            return (
              <div key={org.organization_id}
                onClick={() => handleSelectOrg(org)}
                className="rounded-2xl p-5 cursor-pointer transition-all hover:-translate-y-1 group"
                style={{
                  background: isActive ? 'rgba(201,168,76,0.08)' : '#0d0d14',
                  border: isActive ? '1px solid rgba(201,168,76,0.4)' : '1px solid rgba(255,255,255,0.06)',
                  boxShadow: isActive ? '0 0 40px rgba(201,168,76,0.1)' : 'none'
                }}>
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  {/* Foto + botón cámara */}
                  <div className="relative">
                    {org.picture ? (
                      <img src={org.picture} alt={org.name}
                        className="w-14 h-14 rounded-xl object-cover" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl flex items-center justify-center"
                        style={{ background: isActive ? 'rgba(201,168,76,0.2)' : 'rgba(255,255,255,0.05)' }}>
                        <Building2 size={26} className={isActive ? 'text-[#c9a84c]' : 'text-[#4a4a50]'} />
                      </div>
                    )}
                    <button
                      onClick={(e) => handlePictureClick(e, org.organization_id)}
                      disabled={isUploading}
                      className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c870)' }}
                      title="Cambiar foto">
                      {isUploading
                        ? <div className="w-3 h-3 border border-black border-t-transparent rounded-full animate-spin" />
                        : <Camera size={12} className="text-black" />}
                    </button>
                  </div>

                  {/* Acciones */}
                  <div className="flex items-center gap-2">
                    {isActive && (
                      <span className="flex items-center gap-1 text-[#c9a84c] text-xs font-bold">
                        <Check size={12} /> Activa
                      </span>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(org.organization_id); }}
                      className="text-[#3a3a40] hover:text-red-400 transition-colors p-1">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <h3 className="text-white font-bold text-base mb-1">{org.name}</h3>
                {org.description && <p className="text-[#7a7870] text-xs leading-relaxed">{org.description}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
